import { useAppDispatch, useAppSelector } from 'app/hooks';
import EquipmentPhotoThumbnail from 'components/common/equipment-photo-thumbnail';
import ExistingComponentSpecFields from 'components/common/existing-component-spec-fields';
import FormInput from 'components/common/form-input';
import Modal from 'components/common/modal';
import Button from 'components/inputs/button';
import LabelButton from 'components/inputs/label-button';
import SendPhotoCollectorModal from 'components/project-page/modals/send-photo-collector-modal';
import {
  CATEGORY_CONFIG,
  EQUIPMENT_IMAGE_HINT,
  EquipmentCategory,
  getCategoryForComponentType,
  haveSameAttachmentIds,
  OUTDOOR_UNIT_MCA_NOTE,
  PACKAGED_STANDALONE_NOTE,
  PANEL_IMAGE_HINT,
  PANEL_NOUN,
  SAVE_MINIMUM_FIELDS,
  SPLIT_ALONGSIDE_PACKAGED_NOTE
} from 'components/structure-page/modals/add-equipment-modal-config';
import { existingSystemComponentTypeProperties as componentTypeProperties } from 'data/constants';
import { ExistingSystemComponentType, PartialWithRequired } from 'data/types';
import {
  addElectricalPanel,
  addExistingEquipmentComponent,
  updateElectricalPanel,
  updateExistingSystemComponent
} from 'features/structure';
import { closeEquipmentModal } from 'features/ui';
import useIsEpOnlyPathway from 'hooks/use-is-ep-only-pathway';
import { nanoid } from 'nanoid';
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState
} from 'react';
import { subtypeCodeById } from 'utils/dwelling-suggestion';
import {
  analyzeAndUpdateElectricalPanel,
  analyzeAndUpdateEquipmentComponent,
  determineModelNumberFromAttachmentIds,
  uploadEquipmentImages
} from 'utils/equipment-images';
import {
  componentHasSiblingsInSystem,
  existingSystemAcceptsComponentKind,
  getExistingEquipmentComponents,
  isBlockedSelfContainedTypeChange,
  isExistingCondenserComponent,
  isSelfContainedComponentType,
  systemHasSelfContainedComponent
} from 'utils/existing-equipment';
import { isEmpty } from 'utils/helpers';
import { hasUsableLabelEvidence } from 'utils/label-evidence';
import { dwellingForSubtypeCode } from 'utils/recommendation-tiles-logic';
import {
  parseRefrigerantFamilySupport,
  serializeRefrigerantFamilySupport
} from 'utils/refrigerant-family-support';
import {
  determineSystemComponentFromLabelEvidence,
  determineSystemComponentFromModelNumber
} from 'utils/systems';
import { errorModal } from 'utils/ui';

type ComponentTypeName = Exclude<
  ExistingSystemComponentType['type'],
  undefined
>;

const ANALYZER_HINT = 'Auto-filled — please verify';

// Staleness key for the AI spec lookup: model number AND the user-reported
// manufacturer both feed the analysis, so re-fire only when the pair
// changes. NUL can't be typed into either input, so the join is unambiguous.
const makeLookupKey = (modelNumber: string, manufacturer?: string) =>
  `${modelNumber.trim()}\u0000${(manufacturer ?? '').trim()}`;

// A specs response of null or a bare { type: 'unknown' } carries nothing
// for applyAnalysisResult to land.
const responseHasContent = (
  componentProps: Partial<ExistingSystemComponentType> | null
) =>
  !!componentProps &&
  Object.entries(componentProps).some(
    ([key, value]) =>
      value !== undefined &&
      value !== null &&
      !(key === 'type' && value === 'unknown')
  );

export default function AddEquipmentModal() {
  const dispatch = useAppDispatch();
  const {
    structure,
    structureAttachments,
    existingSystems,
    structureSubtypeId
  } = useAppSelector((state) => state.structure);
  const structureTypes = useAppSelector((state) => state.data.structureTypes);
  const {
    activeLevelIndex,
    equipmentModalKind,
    editingEquipmentComponentId,
    equipmentModalTargetSystemId
  } = useAppSelector((state) => state.ui);
  const fileInputId = useId();

  const open = equipmentModalKind !== null;
  const kind = equipmentModalKind ?? 'electrical-panel';
  const targetSystem = existingSystems.find(
    (system) => system.uniqueId === equipmentModalTargetSystemId
  );
  const editComponent = editingEquipmentComponentId
    ? getExistingEquipmentComponents(existingSystems).find(
        (component) => component.uniqueId === editingEquipmentComponentId
      )
    : undefined;

  // Mixing prevention on the edit path (AHA-6796): a component with
  // siblings in its system must not convert to a packaged (Self Contained)
  // type, so the Packaged Unit category is withheld from the tier-1 select.
  // A standalone single-component system keeps every category — converting
  // the sole component is not mixing. (A legacy mixed row can hold an SC
  // component WITH siblings — its edit still lands on the packaged tier
  // via the stored type, and the tier-2 kindOptions filter below restricts
  // it to its own SC subtype; cursor[bot] PR #4330.)
  const editComponentHasSiblings =
    !!editComponent &&
    componentHasSiblingsInSystem(existingSystems, editComponent.uniqueId);
  const categoryOptions = useMemo(
    () =>
      (Object.keys(CATEGORY_CONFIG) as EquipmentCategory[])
        .filter(
          (categoryOption) =>
            !(editComponentHasSiblings && categoryOption === 'packaged-unit')
        )
        .map((categoryOption) => ({
          label: CATEGORY_CONFIG[categoryOption].noun,
          value: categoryOption
        })),
    [editComponentHasSiblings]
  );

  const [imageAttachmentIds, setImageAttachmentIds] = useState<number[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [modelNumber, setModelNumber] = useState('');
  // Panel amperage (AHA-6681 batch 2 addendum): editable in the modal so the
  // placed-panel icon click on the placement step can change it without a
  // trip back to the Existing Equipment page. Local until Save, like all
  // other modal state.
  const [amperage, setAmperage] = useState<number | undefined>(undefined);
  // Two-tier equipment type (AHA-6681 batch 6): category then kind, chosen
  // IN the modal. All form state stays local until Save.
  const [category, setCategory] = useState<EquipmentCategory | null>(null);
  const [selectedType, setSelectedType] = useState<ComponentTypeName | ''>('');
  const [specValues, setSpecValues] = useState<Record<string, any>>({});
  // Analyzer-sourced fields keep a "please verify" hint until the user edits
  // them; dirty (user-owned) fields are never overwritten by the analyzer.
  // 'type' and 'modelNumber' are tracked as pseudo-keys.
  const [analyzerKeys, setAnalyzerKeys] = useState<Set<string>>(new Set());
  const dirtyKeysRef = useRef<Set<string>>(new Set());
  // 'no-details' is a RESULT state (photo analysis found nothing at all),
  // shown in the same indicator slot as the in-progress states and cleared
  // by the next analysis run, photo add, model-number edit, or modal re-init.
  const [analysisState, setAnalysisState] = useState<
    'idle' | 'reading-photo' | 'looking-up-specs' | 'no-details'
  >('idle');
  const analysisRunIdRef = useRef(0);
  const lastAnalyzedLookupKeyRef = useRef<string | null>(null);
  const selectedTypeRef = useRef<ComponentTypeName | ''>('');
  const [photoCollectorOpen, setPhotoCollectorOpen] = useState(false);
  const [awaitingPhoneUpload, setAwaitingPhoneUpload] = useState(false);
  const baselinePhotoIdsRef = useRef<Set<number>>(new Set());

  // Tier-2 mirror of the categoryOptions filter (AHA-6796, cursor[bot] PR
  // #4330): a legacy mixed row's SC edit lands on the packaged tier via
  // its STORED type even though tier-1 withholds the category, so the kind
  // picker itself must refuse the corrupting direction — the reducer would
  // strip a changed SC type but spread-merge the rest of the payload,
  // wiping the old subtype's specs onto an unchanged type. The shared
  // predicate keeps only the component's own SC subtype (same-type
  // carve-out); keyed off editComponent.type rather than local
  // selectedType so category round-trips inside the modal can't relax the
  // restriction. Non-SC kinds, standalone SC edits, and ADD mode pass
  // through unchanged.
  const kindOptions = useMemo(
    () =>
      category
        ? CATEGORY_CONFIG[category].kinds
            .filter(
              (kindOption) =>
                !isBlockedSelfContainedTypeChange(
                  kindOption.type,
                  editComponent?.type,
                  editComponentHasSiblings
                )
            )
            .map((kindOption) => ({
              label: kindOption.label,
              value: kindOption.type
            }))
        : [],
    [category, editComponent?.type, editComponentHasSiblings]
  );

  const isPanel = kind === 'electrical-panel';
  const isEpOnly = useIsEpOnlyPathway();

  // Belt-and-suspenders (AHA-6681 pathway exclusivity): the entry list
  // already disables the non-panel add buttons on the EP-only pathway
  // (ductless) — this covers stale deep links / races so non-panel
  // categories are never offered in ADD mode. Hybrid is not EP-only.
  // Editing a legacy/persisted non-panel component stays fully
  // functional; panel add/edit is untouched on every path.
  useEffect(() => {
    if (open && !isPanel && !editingEquipmentComponentId && isEpOnly) {
      dispatch(closeEquipmentModal());
    }
  }, [open, isPanel, editingEquipmentComponentId, isEpOnly, dispatch]);
  const editPanelEntry =
    isPanel && editingEquipmentComponentId
      ? structure
          .flatMap((level) =>
            level.electricalPanels.map((panel) => ({
              panel,
              levelUniqueId: level.uniqueId
            }))
          )
          .find((entry) => entry.panel.uniqueId === editingEquipmentComponentId)
      : undefined;
  const editPanel = editPanelEntry?.panel;
  const isEditing = !!editComponent || !!editPanel;
  // Mobile-home compatible default (AHA-6681): dwellingForSubtypeCode is
  // the single source of truth for "manufactured gets the mobile-home
  // treatment"; null/unknown subtype or not-yet-loaded structure types
  // resolve to 'standard' → no seed.
  const isMobileDwelling = useMemo(
    () =>
      dwellingForSubtypeCode(
        subtypeCodeById(structureTypes, structureSubtypeId)
      ) === 'mobile',
    [structureTypes, structureSubtypeId]
  );
  // Render-assigned ref (not a useCallback dep): applySelectedType stays a
  // stable [] callback, and an in-flight analyzer lookup that resolves
  // later still reads the current ADD/edit + dwelling truth.
  const seedMobileHomeDefaultRef = useRef(false);
  seedMobileHomeDefaultRef.current = !isEditing && isMobileDwelling;
  // Same render-assigned pattern for the sibling truth (AHA-6796,
  // cursor[bot] PR #4330): applyAnalysisResult and the async lookup chains
  // hanging off it are stable []-dep callbacks, so an analyzer response
  // resolving later must read the CURRENT sibling state through a ref —
  // not a stale closure — to decide whether an SC type may land.
  const editComponentHasSiblingsRef = useRef(false);
  editComponentHasSiblingsRef.current = editComponentHasSiblings;
  let noun = 'Equipment';
  if (isPanel) {
    noun = PANEL_NOUN;
  } else if (category) {
    noun = CATEGORY_CONFIG[category].noun;
  }
  const title = `${isEditing ? 'Edit' : 'Add'} ${noun}`;
  const buttonText = isEditing ? 'Save' : 'Add';
  const seedCategory: EquipmentCategory | null = isPanel ? null : kind;

  // Panels stay level-nested (schema requires a level) but the floor select
  // is gone (AHA-6681 batch 6): new panels silently default to the active
  // level, edits keep their level — level moves return with batch 2
  // drag-to-place.
  const defaultPanelLevelUniqueId =
    editPanelEntry?.levelUniqueId ??
    structure[activeLevelIndex]?.uniqueId ??
    structure[0]?.uniqueId;

  const initializedEditKeyRef = useRef<string | null>(null);

  const openState: [boolean, Dispatch<SetStateAction<boolean>>] = [
    open,
    (value) => {
      const next = typeof value === 'function' ? value(open) : value;
      if (!next) {
        dispatch(closeEquipmentModal());
      }
    }
  ];

  // Sets the type and drops spec values / analyzer / dirty marks for keys
  // the new type's config doesn't know (same semantics as the details form's
  // handleTypeChange).
  const applySelectedType = useCallback((newType: ComponentTypeName | '') => {
    const newConfig = (newType && componentTypeProperties[newType]) || {};
    selectedTypeRef.current = newType;
    setSelectedType(newType);
    setSpecValues((current) => {
      const filtered = Object.fromEntries(
        Object.entries(current).filter(([key]) => key in newConfig)
      );
      // Mobile-home compatible default (AHA-6681): on ADD for a mobile/
      // manufactured dwelling, seed Yes when the new type knows the field
      // and no value survived the filter. A plain value on purpose — not
      // dirty, not analyzer-marked — so analyzer and user input both win.
      if (
        seedMobileHomeDefaultRef.current &&
        'mobileHomeCompatible' in newConfig &&
        !('mobileHomeCompatible' in filtered)
      ) {
        filtered.mobileHomeCompatible = true;
      }
      return filtered;
    });
    setAnalyzerKeys(
      (current) =>
        new Set(
          [...current].filter(
            (key) => key === 'type' || key === 'modelNumber' || key in newConfig
          )
        )
    );
    dirtyKeysRef.current = new Set(
      [...dirtyKeysRef.current].filter(
        (key) => key === 'type' || key === 'modelNumber' || key in newConfig
      )
    );
  }, []);

  // Lands an analyzer response into the local form: type first (unless the
  // user owns it), then every returned spec key the selected type's config
  // knows and the user hasn't touched — each marked analyzer-sourced.
  const applyAnalysisResult = useCallback(
    (componentProps: Partial<ExistingSystemComponentType>) => {
      const returnedType = componentProps.type;
      if (
        returnedType &&
        returnedType !== 'unknown' &&
        returnedType in componentTypeProperties &&
        !dirtyKeysRef.current.has('type') &&
        returnedType !== selectedTypeRef.current &&
        // SC mixing guard (AHA-6796, cursor[bot] PR #4330): an analyzer-
        // returned Self Contained type must not land on the edit of a
        // component that has split siblings — the Packaged Unit category is
        // already withheld from the pickers for exactly that case, and the
        // analyzer path must not bypass the filter. The returned SPEC keys
        // need no special handling: with the type skipped, effectiveType
        // stays whatever is selected (usually '' on an untyped legacy
        // edit), so spec keys only land if the currently selected type's
        // config knows them — the skipped SC type is simply treated like a
        // user-owned type, and SC-only specs have no config to land in.
        !(
          isSelfContainedComponentType(returnedType) &&
          editComponentHasSiblingsRef.current
        )
      ) {
        applySelectedType(returnedType);
        // Non-taxonomy types (PTAC, Ductless, ...) map to null and render
        // the raw-type row instead of the tiers; Self Contained lands on
        // the packaged-unit tier (AHA-6796).
        setCategory(getCategoryForComponentType(returnedType));
        setAnalyzerKeys((current) => new Set(current).add('type'));
      }
      const effectiveType = selectedTypeRef.current;
      if (!effectiveType) {
        return;
      }
      const typeConfig = componentTypeProperties[effectiveType] ?? {};
      // Select/multiselect prefill guard (AHA-6681 spec-field reorg):
      // analyzer values outside a select config's vocabulary are dropped
      // instead of landing an unrenderable value. Boolean selects are exempt
      // — their options are the Yes/No display vocabulary while the stored
      // value is a boolean. Multiselect values go through the lenient parser
      // and are kept (canonically re-serialized) if any token survives.
      const analyzedEntries = Object.entries(componentProps)
        .filter(
          ([key, value]) =>
            key in typeConfig &&
            !dirtyKeysRef.current.has(key) &&
            value !== undefined &&
            value !== null
        )
        .flatMap(([key, value]): [string, any][] => {
          const config = (typeConfig as Record<string, any>)[key];
          if (config.type === 'multiselect') {
            const parsed = parseRefrigerantFamilySupport(value);
            return parsed.length > 0
              ? [[key, serializeRefrigerantFamilySupport(parsed)]]
              : [];
          }
          if (
            config.type === 'select' &&
            !config.isBoolean &&
            Array.isArray(config.options) &&
            !config.options.some((option: any) =>
              typeof option === 'object' && option !== null
                ? option.value === value
                : option === value
            )
          ) {
            return [];
          }
          return [[key, value]];
        });
      if (analyzedEntries.length === 0) {
        return;
      }
      setSpecValues((current) => ({
        ...current,
        ...Object.fromEntries(analyzedEntries)
      }));
      setAnalyzerKeys(
        (current) =>
          new Set([...current, ...analyzedEntries.map(([key]) => key)])
      );
    },
    [applySelectedType]
  );

  const runModelNumberLookup = useCallback(
    async (candidate: string, manufacturer?: string) => {
      analysisRunIdRef.current += 1;
      const runId = analysisRunIdRef.current;
      setAnalysisState('looking-up-specs');
      try {
        // Two call shapes on purpose: without a manufacturer the lookup is
        // invoked exactly as before (single argument), keeping the
        // manufacturer-less path byte-identical.
        const componentProps = manufacturer
          ? await determineSystemComponentFromModelNumber(
              candidate,
              manufacturer
            )
          : await determineSystemComponentFromModelNumber(candidate);
        if (runId !== analysisRunIdRef.current) {
          return;
        }
        if (componentProps) {
          applyAnalysisResult(componentProps);
        }
      } catch (error) {
        console.error(
          'Error looking up specifications from model number:',
          error
        );
      } finally {
        if (runId === analysisRunIdRef.current) {
          setAnalysisState('idle');
        }
      }
    },
    [applyAnalysisResult]
  );

  // Photo pipeline (computer upload + phone collector): extract a model
  // number AND the transcribed label evidence, land the model number in the
  // field, then chain into the spec lookup — grounded by the evidence, or
  // evidence-only when no model number was readable — all while the modal
  // stays open and editable. Stale responses are dropped by the run id;
  // user-owned fields win via dirtyKeysRef.
  const analyzePhotos = useCallback(
    async (candidateIds: number[]) => {
      // A new photo restarts the story: a lingering no-details notice is
      // stale even when the guards below skip re-analysis.
      setAnalysisState((current) =>
        current === 'no-details' ? 'idle' : current
      );
      if (isPanel || candidateIds.length === 0 || modelNumber.trim()) {
        return;
      }
      analysisRunIdRef.current += 1;
      const runId = analysisRunIdRef.current;
      setAnalysisState('reading-photo');
      try {
        const { modelNumber: extracted, evidence } =
          await determineModelNumberFromAttachmentIds(candidateIds);
        if (runId !== analysisRunIdRef.current) {
          return;
        }
        const usableEvidence = hasUsableLabelEvidence(evidence)
          ? evidence
          : null;
        if (!extracted && !usableEvidence) {
          setAnalysisState('no-details');
          return;
        }
        const manufacturer = (
          (specValues.manufacturer as string | undefined) ?? ''
        ).trim();
        setAnalysisState('looking-up-specs');
        let componentProps: Partial<ExistingSystemComponentType> | null;
        if (extracted) {
          if (!dirtyKeysRef.current.has('modelNumber')) {
            setModelNumber(extracted);
            setAnalyzerKeys((current) => new Set(current).add('modelNumber'));
          }
          lastAnalyzedLookupKeyRef.current = makeLookupKey(
            extracted,
            manufacturer
          );
          if (usableEvidence) {
            componentProps = await determineSystemComponentFromModelNumber(
              extracted,
              manufacturer || undefined,
              usableEvidence
            );
          } else {
            // Two call shapes on purpose: without a manufacturer the lookup
            // is invoked exactly as before (single argument), keeping the
            // manufacturer-less path byte-identical.
            componentProps = manufacturer
              ? await determineSystemComponentFromModelNumber(
                  extracted,
                  manufacturer
                )
              : await determineSystemComponentFromModelNumber(extracted);
          }
        } else {
          // Evidence-only: there is no model number to key the (modelNumber,
          // manufacturer) staleness pair on, so lastAnalyzedLookupKeyRef is
          // deliberately left untouched.
          componentProps = await determineSystemComponentFromLabelEvidence(
            usableEvidence!,
            manufacturer || undefined
          );
        }
        if (runId !== analysisRunIdRef.current) {
          return;
        }
        // The label's brand seeds the manufacturer only when the specs
        // response did not carry one — one apply path, so the analyzerKeys
        // hint and dirtyKeysRef user-wins treatment come free and nothing
        // double-applies. (Cast: manufacturer only exists on some members
        // of the ExistingSystemComponentType union.)
        const returnedManufacturer = (
          componentProps as { manufacturer?: string } | null
        )?.manufacturer;
        const brandManufacturer =
          usableEvidence?.brandText && !returnedManufacturer
            ? { manufacturer: usableEvidence.brandText }
            : {};
        // manufactureDate is copied deterministically from the label
        // evidence (AHA-6796) — the specs prompt never returns it, and the
        // verbatim transcription outranks any echo, so it spreads AFTER
        // componentProps. Dirty-key precedence in applyAnalysisResult still
        // protects a user-entered value.
        const evidenceManufactureDate = usableEvidence?.manufactureDate
          ? { manufactureDate: usableEvidence.manufactureDate }
          : {};
        const mergedProps = {
          ...brandManufacturer,
          ...(componentProps ?? {}),
          ...evidenceManufactureDate
        };
        if (responseHasContent(mergedProps)) {
          applyAnalysisResult(mergedProps);
        } else if (!extracted) {
          // The evidence-only lookup came back empty: the analyzer filled
          // nothing at all, which IS the no-details case.
          setAnalysisState('no-details');
        }
      } catch (error) {
        console.error('Error analyzing equipment photos:', error);
      } finally {
        if (runId === analysisRunIdRef.current) {
          setAnalysisState((current) =>
            current === 'no-details' ? current : 'idle'
          );
        }
      }
    },
    [isPanel, modelNumber, specValues.manufacturer, applyAnalysisResult]
  );

  useEffect(() => {
    if (!open) {
      initializedEditKeyRef.current = null;
      return;
    }
    const editKey = editingEquipmentComponentId ?? 'new';
    if (initializedEditKeyRef.current === editKey) {
      return;
    }
    initializedEditKeyRef.current = editKey;
    setIsUploading(false);
    setPhotoCollectorOpen(false);
    setAwaitingPhoneUpload(false);
    setAnalysisState('idle');
    analysisRunIdRef.current += 1;
    baselinePhotoIdsRef.current = new Set();
    dirtyKeysRef.current = new Set();
    setAnalyzerKeys(new Set());
    setImageAttachmentIds(
      editComponent?.imageAttachmentIds ?? editPanel?.imageAttachmentIds ?? []
    );
    setAmperage(editPanel?.amperage);
    setModelNumber(editComponent?.modelNumber ?? '');
    lastAnalyzedLookupKeyRef.current = editComponent?.modelNumber?.trim()
      ? makeLookupKey(
          editComponent.modelNumber,
          (editComponent as any).manufacturer as string | undefined
        )
      : null;

    if (editComponent) {
      const editType =
        editComponent.typeSpecified !== false &&
        editComponent.type &&
        editComponent.type !== 'unknown' &&
        editComponent.type in componentTypeProperties
          ? editComponent.type
          : '';
      // Untyped legacy components keep only their category seed; typed ones
      // land on their actual category/kind (null category = raw-type row).
      let editCategory: EquipmentCategory | null;
      if (editType) {
        editCategory = getCategoryForComponentType(editType);
      } else {
        editCategory = isExistingCondenserComponent(editComponent)
          ? 'outdoor-unit'
          : 'indoor-unit';
      }
      selectedTypeRef.current = editType;
      setSelectedType(editType);
      setCategory(editCategory);
      const typeConfig = (editType && componentTypeProperties[editType]) || {};
      const values: Record<string, any> = {};
      Object.keys(typeConfig).forEach((key) => {
        const value = (editComponent as any)[key];
        if (value !== undefined && value !== null) {
          values[key] = value;
          // Every populated saved field is user-owned: analysis only fills
          // blanks on the edit path.
          dirtyKeysRef.current.add(key);
        }
      });
      setSpecValues(values);
      if (editType) {
        dirtyKeysRef.current.add('type');
      }
      if (editComponent.modelNumber) {
        dirtyKeysRef.current.add('modelNumber');
      }
    } else {
      selectedTypeRef.current = '';
      setSelectedType('');
      setCategory(isPanel ? null : kind);
      setSpecValues({});
    }
  }, [
    open,
    editingEquipmentComponentId,
    editComponent,
    editPanel,
    kind,
    isPanel
  ]);

  // Late dwelling resolution re-seed (AHA-6681): structureTypes arrives in
  // the non-blocking secondary fetch batch, so the dwelling can resolve to
  // mobile AFTER the user has already picked a type — and applySelectedType
  // (the only other seed site) never re-runs on its own. This effect heals
  // exactly that ordering: when isMobileDwelling flips true with a
  // qualifying type already selected, seed the same plain `true` the
  // in-applySelectedType seed would have landed. Declared AFTER the
  // modal-init effect so init's state resets flush first on reopen.
  //
  // Guards, in order: modal open; never on the edit path; dwelling is
  // mobile; a type is selected whose config knows the field; and the user
  // doesn't own the field (a set-then-cleared value is an intentional
  // clear — no re-seed over it). The "value already present" check — an
  // analyzer or user value, or the applySelectedType seed itself — lives
  // INSIDE the functional updater via `'mobileHomeCompatible' in current`
  // (same `in` semantics as the existing seed), which keeps specValues out
  // of the dep list and returns `current` unchanged when there is nothing
  // to do. Like the existing seed, the value is plain: NOT analyzer-marked,
  // NOT dirty — analyzer results and user edits both still win.
  //
  // Known-benign transient: on ADD-reopen, a leftover selectedType from the
  // prior session can seed once before the init effect's queued resets
  // re-render. The value lands in the freshly-reset specValues, but it is
  // exactly what the next qualifying type pick would seed anyway, and
  // applySelectedType's key filter drops it on a non-qualifying pick — so
  // no guard is added for it.
  useEffect(() => {
    if (!open || isEditing || !isMobileDwelling || !selectedType) {
      return;
    }
    const config = componentTypeProperties[selectedType] ?? {};
    if (
      !('mobileHomeCompatible' in config) ||
      dirtyKeysRef.current.has('mobileHomeCompatible')
    ) {
      return;
    }
    setSpecValues((current) =>
      'mobileHomeCompatible' in current
        ? current
        : { ...current, mobileHomeCompatible: true }
    );
  }, [open, isEditing, isMobileDwelling, selectedType]);

  useEffect(() => {
    if (!awaitingPhoneUpload) {
      return;
    }
    const baseline = baselinePhotoIdsRef.current;
    const newIds = structureAttachments
      .map((attachment) => attachment.id)
      .filter((id) => !baseline.has(id) && !imageAttachmentIds.includes(id));
    if (newIds.length > 0) {
      setImageAttachmentIds((current) => [
        ...current,
        ...newIds.filter((id) => !current.includes(id))
      ]);
      analyzePhotos(newIds);
    }
  }, [
    structureAttachments,
    awaitingPhoneUpload,
    imageAttachmentIds,
    analyzePhotos
  ]);

  const assignedAttachments = structureAttachments.filter((attachment) =>
    imageAttachmentIds.includes(attachment.id)
  );

  const handleUpload = async (files: File[]) => {
    setIsUploading(true);
    try {
      const uploadedIds = await uploadEquipmentImages(files);
      setImageAttachmentIds((current) => [
        ...current,
        ...uploadedIds.filter((id) => !current.includes(id))
      ]);
      analyzePhotos(uploadedIds);
    } catch (error) {
      console.error('Error uploading equipment images:', error);
      errorModal('Failed to upload equipment images');
    } finally {
      setIsUploading(false);
    }
  };

  const handlePhoneTextSent = () => {
    baselinePhotoIdsRef.current = new Set([
      ...structureAttachments.map((attachment) => attachment.id),
      ...imageAttachmentIds
    ]);
    setAwaitingPhoneUpload(true);
  };

  const clearAnalyzerKey = (key: string) => {
    setAnalyzerKeys((current) => {
      const next = new Set(current);
      next.delete(key);
      return next;
    });
  };

  const handleCategoryChange = (value: string) => {
    const newCategory = value as EquipmentCategory;
    dirtyKeysRef.current.add('type');
    clearAnalyzerKey('type');
    setCategory(newCategory);
    const typeBelongsToCategory = CATEGORY_CONFIG[newCategory].kinds.some(
      (kindOption) => kindOption.type === selectedTypeRef.current
    );
    if (!typeBelongsToCategory) {
      applySelectedType('');
    }
  };

  const handleKindChange = (value: string) => {
    dirtyKeysRef.current.add('type');
    clearAnalyzerKey('type');
    applySelectedType(value as ComponentTypeName);
  };

  const handleResetType = () => {
    dirtyKeysRef.current.add('type');
    clearAnalyzerKey('type');
    setCategory(seedCategory);
    applySelectedType('');
  };

  const handleSpecChange = (key: string, value: any) => {
    dirtyKeysRef.current.add(key);
    clearAnalyzerKey(key);
    setSpecValues((current) => ({ ...current, [key]: value }));
  };

  const handleModelNumberChange = (value: string) => {
    dirtyKeysRef.current.add('modelNumber');
    clearAnalyzerKey('modelNumber');
    // Typing a model number supersedes the "couldn't read one" notice.
    setAnalysisState((current) =>
      current === 'no-details' ? 'idle' : current
    );
    setModelNumber(value);
  };

  // Model-number analysis fires on blur / Enter (not per keystroke). The
  // committed value is read straight off the input — the TextInput debounces
  // its onChange, so local state can lag one tick behind. The staleness key
  // is the (modelNumber, manufacturer) pair: the current manufacturer rides
  // along into the lookup.
  const commitModelNumber = (rawValue: string) => {
    const trimmed = rawValue.trim();
    if (isPanel || !trimmed) {
      return;
    }
    const manufacturer = (
      (specValues.manufacturer as string | undefined) ?? ''
    ).trim();
    const lookupKey = makeLookupKey(trimmed, manufacturer);
    if (lookupKey === lastAnalyzedLookupKeyRef.current) {
      return;
    }
    lastAnalyzedLookupKeyRef.current = lookupKey;
    runModelNumberLookup(trimmed, manufacturer || undefined);
  };

  // Manufacturer commits mirror the model-number pattern (blur / Enter,
  // value read off the input). A manufacturer edit re-fires the SAME lookup
  // — the endpoint requires a model number, so without one this never
  // fires. The user-typed manufacturer is dirty, so the re-fired analysis
  // cannot overwrite it (applyAnalysisResult's dirty-key guard).
  const commitManufacturer = (rawValue: string) => {
    const trimmedModelNumber = modelNumber.trim();
    if (isPanel || !trimmedModelNumber) {
      return;
    }
    const manufacturer = rawValue.trim();
    const lookupKey = makeLookupKey(trimmedModelNumber, manufacturer);
    if (lookupKey === lastAnalyzedLookupKeyRef.current) {
      return;
    }
    lastAnalyzedLookupKeyRef.current = lookupKey;
    runModelNumberLookup(trimmedModelNumber, manufacturer || undefined);
  };

  const formMinimumMet = (() => {
    if (isPanel) {
      return !!defaultPanelLevelUniqueId;
    }
    // Save gate = the per-category ruling minimum (a subset of the config
    // `required` set, which keeps driving the missing-details warning and
    // page progress after save).
    if (!selectedType) {
      return false;
    }
    const minimumKeys = SAVE_MINIMUM_FIELDS[selectedType] ?? [];
    return minimumKeys.every((key) => !isEmpty(specValues[key]));
  })();
  const saveMinimumMet =
    formMinimumMet || (!isPanel && imageAttachmentIds.length > 0);

  const handleSave = () => {
    if (!saveMinimumMet) {
      return;
    }
    analysisRunIdRef.current += 1;
    const hasImage = imageAttachmentIds.length > 0;
    const photoBackedIncomplete = !isPanel && hasImage && !formMinimumMet;

    if (isPanel) {
      if (editPanelEntry) {
        const imagesChanged = !haveSameAttachmentIds(
          editPanelEntry.panel.imageAttachmentIds ?? [],
          imageAttachmentIds
        );
        const amperageChanged = amperage !== editPanelEntry.panel.amperage;
        const updates: {
          amperage?: number;
          imageAttachmentIds?: number[];
          analyzing?: boolean;
        } = {};
        if (imagesChanged) {
          updates.imageAttachmentIds = imageAttachmentIds;
          updates.analyzing = hasImage;
        }
        if (amperageChanged) {
          updates.amperage = amperage;
        }
        if (Object.keys(updates).length > 0) {
          dispatch(
            updateElectricalPanel({
              uniqueId: editPanelEntry.panel.uniqueId,
              updates
            })
          );
        }
        if (imagesChanged && hasImage) {
          // Panel amperage analysis stays post-save (ruling allows).
          analyzeAndUpdateElectricalPanel(editPanelEntry.panel.uniqueId);
        }
        return;
      }

      const uniqueId = nanoid(5);
      dispatch(
        addElectricalPanel({
          levelUniqueId: defaultPanelLevelUniqueId!,
          electricalPanel: {
            uniqueId,
            ...(amperage !== undefined ? { amperage } : {}),
            imageAttachmentIds: hasImage ? imageAttachmentIds : undefined,
            analyzing: hasImage
          }
        })
      );
      if (hasImage) {
        analyzeAndUpdateElectricalPanel(uniqueId);
      }
      return;
    }

    const trimmedModelNumber = modelNumber.trim();
    const hasModelNumber = trimmedModelNumber.length > 0;
    const uniqueId = editComponent?.uniqueId ?? nanoid(5);
    const persistTypeSpecified = !!selectedType;
    const persistAnalyzing = photoBackedIncomplete;

    if (editComponent && selectedType) {
      // Belt-and-braces (AHA-6796, cursor[bot] PR #4330): unreachable
      // through the UI — tier-1 withholds the Packaged Unit category and
      // the tier-2 kindOptions filter keeps a sibling'd SC edit on its own
      // subtype — but if a future picker regression let a blocked SC type
      // through, the reducer would strip type/typeSpecified while
      // spread-merging the rest, landing clearedOldTypeKeys/specPayload
      // alone and silently corrupting the component's specs. The Modal
      // closes on save regardless (closeOnSave default), so a bare return
      // would look like success — errorModal keeps the regression loud.
      if (
        isBlockedSelfContainedTypeChange(
          selectedType,
          editComponent.type,
          componentHasSiblingsInSystem(existingSystems, editComponent.uniqueId)
        )
      ) {
        errorModal(
          'This equipment shares a system with other components, so it ' +
            'cannot be converted to a packaged unit.'
        );
        return;
      }
    }

    if (!selectedType) {
      if (editComponent) {
        dispatch(
          updateExistingSystemComponent({
            uniqueId,
            typeSpecified: false,
            analyzing: persistAnalyzing,
            modelNumber: hasModelNumber ? trimmedModelNumber : undefined,
            imageAttachmentIds: hasImage ? imageAttachmentIds : undefined
          })
        );
      } else {
        dispatch(
          addExistingEquipmentComponent({
            component: {
              uniqueId,
              type: 'unknown',
              typeSpecified: false,
              analyzing: persistAnalyzing,
              replacing: true,
              modelNumber: hasModelNumber ? trimmedModelNumber : undefined,
              imageAttachmentIds: hasImage ? imageAttachmentIds : undefined
            } as ExistingSystemComponentType,
            newSystemUniqueId: nanoid(5),
            targetSystemUniqueId: targetSystem?.uniqueId
          })
        );
      }
    } else {
      const typeConfig = componentTypeProperties[selectedType] ?? {};
      // All of the selected type's config keys: reviewed values persist,
      // cleared fields clear. Completed forms stay analyzing: false with no
      // post-save analyzer run so user-reviewed fields are not overwritten.
      const specPayload: Record<string, any> = {};
      Object.keys(typeConfig).forEach((key) => {
        const value = specValues[key];
        specPayload[key] = isEmpty(value) ? undefined : value;
      });

      if (editComponent) {
        const oldConfig =
          (editComponent.type && componentTypeProperties[editComponent.type]) ||
          {};
        const clearedOldTypeKeys: Record<string, undefined> = {};
        Object.keys(oldConfig).forEach((key) => {
          if (!(key in typeConfig)) {
            clearedOldTypeKeys[key] = undefined;
          }
        });
        // position/levelUniqueId deliberately omitted: the spread-merge
        // reducer preserves an already-placed legacy component's placement,
        // and a new one stays unplaced.
        dispatch(
          updateExistingSystemComponent({
            uniqueId,
            type: selectedType,
            typeSpecified: persistTypeSpecified,
            analyzing: persistAnalyzing,
            modelNumber: hasModelNumber ? trimmedModelNumber : undefined,
            imageAttachmentIds: hasImage ? imageAttachmentIds : undefined,
            // SC is always replacing (AHA-6796): an edit save heals legacy
            // analyzer-created SC components whose card toggle was flipped
            // before the toggle was locked.
            ...(isSelfContainedComponentType(selectedType)
              ? { replacing: true }
              : {}),
            ...clearedOldTypeKeys,
            ...specPayload
          } as PartialWithRequired<ExistingSystemComponentType, 'uniqueId'>)
        );
      } else {
        const definedSpecs = Object.fromEntries(
          Object.entries(specPayload).filter(([, value]) => value !== undefined)
        );
        dispatch(
          addExistingEquipmentComponent({
            component: {
              uniqueId,
              type: selectedType,
              typeSpecified: persistTypeSpecified,
              analyzing: persistAnalyzing,
              replacing: true,
              modelNumber: hasModelNumber ? trimmedModelNumber : undefined,
              imageAttachmentIds: hasImage ? imageAttachmentIds : undefined,
              ...definedSpecs
            } as ExistingSystemComponentType,
            newSystemUniqueId: nanoid(5),
            targetSystemUniqueId: targetSystem?.uniqueId
          })
        );
      }
    }

    if (photoBackedIncomplete) {
      analyzeAndUpdateEquipmentComponent(uniqueId);
    }
  };

  const showRawTypeRow =
    !isPanel && !!selectedType && !getCategoryForComponentType(selectedType);

  // Standalone-system routing notes (AHA-6796), ADD mode only. Packaged
  // direction: an SC add never joins a populated system, so once any ducted
  // system has components the save provably lands in its own system — say
  // so. Split direction: the reducer targets the first ducted system
  // WITHOUT SC components, so the mirror note shows only when no such
  // split-eligible system exists — then the reducer provably creates a new
  // system. When an SC-containing system merely sits before an eligible
  // one, the add JOINS that later system, so no note is shown. An add
  // opened from a system's own tiles whose target accepts the category
  // lands there, so neither note applies.
  const firstSplitEligibleIndex = existingSystems.findIndex(
    (system) =>
      system.type === 'ducted' && !systemHasSelfContainedComponent(system)
  );
  const splitAddCreatesNewSystem =
    firstSplitEligibleIndex === -1 &&
    existingSystems.some(
      (system) =>
        system.type === 'ducted' && systemHasSelfContainedComponent(system)
    );
  const targetAcceptsCategory =
    !!targetSystem &&
    !!category &&
    existingSystemAcceptsComponentKind(
      targetSystem,
      category === 'packaged-unit'
    );
  const standaloneNotesApply = !isEditing && !targetAcceptsCategory;
  let standaloneNote: string | null = null;
  if (standaloneNotesApply && category === 'packaged-unit') {
    if (
      existingSystems.some(
        (system) => system.type === 'ducted' && system.components.length > 0
      )
    ) {
      standaloneNote = PACKAGED_STANDALONE_NOTE;
    }
  } else if (standaloneNotesApply && category && splitAddCreatesNewSystem) {
    standaloneNote = SPLIT_ALONGSIDE_PACKAGED_NOTE;
  }

  return (
    <Modal
      openState={openState}
      title={title}
      buttonText={buttonText}
      saveDisabled={!saveMinimumMet}
      onSave={handleSave}
      size='wide'
    >
      <div className='flex flex-col gap-4'>
        <div className='flex flex-col gap-2'>
          <span className='font-semibold text-gray-800'>Photo (optional)</span>
          <div className='flex flex-wrap items-center gap-3'>
            {assignedAttachments.map((attachment) => (
              <EquipmentPhotoThumbnail
                key={attachment.id}
                src={attachment.url ?? ''}
                alt={attachment.name}
                size='sm'
                onRemove={() =>
                  setImageAttachmentIds((current) =>
                    current.filter((id) => id !== attachment.id)
                  )
                }
              />
            ))}
            <input
              id={fileInputId}
              type='file'
              accept='image/*'
              onChange={(e) => {
                const { files } = e.currentTarget;
                if (files && files.length > 0) {
                  const captured = Array.from(files);
                  e.currentTarget.value = '';
                  handleUpload(captured);
                } else {
                  e.currentTarget.value = '';
                }
              }}
              className='hidden'
              multiple
            />
            {assignedAttachments.length === 0 && (
              <>
                <LabelButton
                  text={isPanel ? 'Upload Photo' : 'Upload from computer'}
                  htmlFor={fileInputId}
                  color='white'
                  loading={isUploading}
                  disabled={isUploading}
                />
                {!isPanel && (
                  <>
                    <span className='text-sm text-gray-500'>or</span>
                    <Button
                      text='Upload from phone'
                      color='white'
                      onClick={() => setPhotoCollectorOpen(true)}
                    />
                  </>
                )}
              </>
            )}
          </div>
          {awaitingPhoneUpload && assignedAttachments.length === 0 && (
            <span className='text-sm text-blue-600'>
              Waiting for a photo from your phone... it will appear here
              automatically once uploaded.
            </span>
          )}
          <span className='text-sm text-gray-500'>
            {isPanel ? PANEL_IMAGE_HINT : EQUIPMENT_IMAGE_HINT}
          </span>
        </div>

        {isPanel && (
          <div className='flex flex-wrap items-center gap-x-5 gap-y-3'>
            <FormInput
              type='number'
              label='Amperage'
              value={amperage}
              onChange={(value) => setAmperage(value as number | undefined)}
              placeholder='0'
              suffix='A'
              min={0}
              disabled={editPanel?.analyzing}
            />
          </div>
        )}

        {!isPanel &&
          (showRawTypeRow ? (
            <div className='flex flex-col gap-2'>
              <span className='font-semibold text-gray-800'>
                Equipment Type
              </span>
              <div className='flex flex-wrap items-center gap-3'>
                <span className='text-gray-800'>{selectedType}</span>
                {analyzerKeys.has('type') && (
                  <span className='text-sm text-gray-500'>{ANALYZER_HINT}</span>
                )}
                <Button
                  text='Change type'
                  color='white'
                  onClick={handleResetType}
                />
              </div>
            </div>
          ) : (
            <div className='flex flex-wrap items-start gap-x-5 gap-y-3'>
              <FormInput
                type='select'
                label='Equipment Type'
                options={categoryOptions}
                value={category ?? ''}
                onChange={(value) => handleCategoryChange(value as string)}
                placeholder='Select type...'
                preventDeselection
                enableChips
                required
              />
              {category && (
                <FormInput
                  type='select'
                  label={`Which ${CATEGORY_CONFIG[
                    category
                  ].noun.toLowerCase()}?`}
                  subLabel={
                    analyzerKeys.has('type') ? ANALYZER_HINT : undefined
                  }
                  options={kindOptions}
                  value={selectedType}
                  onChange={(value) => handleKindChange(value as string)}
                  preventDeselection
                  enableChips
                  required
                />
              )}
              {standaloneNote && (
                // w-full breaks the note out of the flex-wrap select row
                // onto its own line (same trick as the MCA note below).
                <div className='w-full'>
                  <span className='text-sm text-gray-500'>
                    {standaloneNote}
                  </span>
                </div>
              )}
            </div>
          ))}

        {!isPanel && (
          <div className='flex flex-wrap items-start gap-x-5 gap-y-3'>
            {/* Manufacturer must stay a SIBLING of this div: its blur/keydown
                handlers commit any inner INPUT's value as a model number.
                Manufacturer has its own sibling commit div (commitManufacturer)
                that re-fires the lookup with the pair. */}
            <div
              className='flex flex-col gap-1'
              onBlur={(e) => {
                const target = e.target as HTMLInputElement;
                if (target.tagName === 'INPUT') {
                  commitModelNumber(target.value);
                }
              }}
              onKeyDown={(e) => {
                const target = e.target as HTMLInputElement;
                if (e.key === 'Enter' && target.tagName === 'INPUT') {
                  commitModelNumber(target.value);
                }
              }}
            >
              <FormInput
                type='text'
                label='Model Number (optional)'
                subLabel={
                  analyzerKeys.has('modelNumber') ? ANALYZER_HINT : undefined
                }
                value={modelNumber}
                onChange={(value) => handleModelNumberChange(value as string)}
                placeholder='Enter Model #'
              />
              {analysisState === 'reading-photo' && (
                <span className='text-sm text-blue-600'>
                  Reading model number from photo...
                </span>
              )}
              {analysisState === 'looking-up-specs' && (
                <span className='text-sm text-blue-600'>
                  Looking up specifications from the model number...
                </span>
              )}
              {analysisState === 'no-details' && (
                <span className='text-sm text-gray-600'>
                  {"We couldn't read a model number from this photo. The " +
                    'data or rating label is usually inside the front ' +
                    'access panel — snap a photo of that label and ' +
                    "we'll fill in the details automatically."}
                </span>
              )}
            </div>
            <div
              className='flex flex-col gap-1'
              onBlur={(e) => {
                const target = e.target as HTMLInputElement;
                if (target.tagName === 'INPUT') {
                  commitManufacturer(target.value);
                }
              }}
              onKeyDown={(e) => {
                const target = e.target as HTMLInputElement;
                if (e.key === 'Enter' && target.tagName === 'INPUT') {
                  commitManufacturer(target.value);
                }
              }}
            >
              <FormInput
                type='text'
                label='Manufacturer'
                subLabel={
                  analyzerKeys.has('manufacturer') ? ANALYZER_HINT : undefined
                }
                value={specValues.manufacturer ?? ''}
                onChange={(value) => handleSpecChange('manufacturer', value)}
                placeholder='Enter Manufacturer'
              />
            </div>
          </div>
        )}

        {!isPanel && selectedType && (
          <div className='flex flex-wrap items-center gap-x-5 gap-y-3'>
            <ExistingComponentSpecFields
              componentType={selectedType}
              values={specValues}
              onChange={handleSpecChange}
              getFieldSubLabel={(key) =>
                analyzerKeys.has(key) ? ANALYZER_HINT : undefined
              }
            />
            {getCategoryForComponentType(selectedType) === 'outdoor-unit' && (
              // w-full breaks the note out of the flex-wrap spec row onto its
              // own line (same trick as the advanced-toggle div).
              <div className='w-full'>
                <span className='text-sm text-gray-500'>
                  {OUTDOOR_UNIT_MCA_NOTE}
                </span>
              </div>
            )}
          </div>
        )}
      </div>
      {!isPanel && (
        <SendPhotoCollectorModal
          openState={[photoCollectorOpen, setPhotoCollectorOpen]}
          subject='existing_equipment'
          onSent={handlePhoneTextSent}
        />
      )}
    </Modal>
  );
}
